import {lazy, Suspense, useEffect, useRef} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {useAuth} from '@api/auth';
import {useActivityHeartbeat, useProductEventsFlush} from '@api/user-metrics';
import {useAtom, useAtomValue} from 'jotai';
import {
  aiPanelOpenAtom,
  aiPushModeAtom,
  contentWidthAtom,
  dashCallOpenAtom,
  meetingDetailOpenAtom,
  offlineModeAtom,
} from '@lib/atoms';
import {Sidebar} from './Sidebar';
import {Topbar} from './Topbar';
import {OfflineBanner} from './OfflineBanner';
import {CookieBanner} from './CookieBanner';
import {OfflinePage} from './OfflinePage';
import {NotificationsDrawer} from '@widgets/notifications-drawer/ui/NotificationsDrawer';
import {AiPanel} from '@widgets/ai-panel/ui/AiPanel';
import {TaskCreateModal} from '@features/manage-tasks/ui/TaskCreateModal';
import {TaskDetailModal} from '@features/manage-tasks/ui/TaskDetailModal';
import {ManagerDetailModal} from '@features/view-manager/ui/ManagerDetailModal';
import {NotificationSettingsModal} from '@features/notification-settings';
import {AppFooter} from './AppFooter';
import {ThemeController} from './ThemeController';
import {useWorkspaceUiScope} from '../model/useWorkspaceUiScope';
import {ErrorState} from '@ui/primitives/ErrorState';
import {PageSkeleton} from '@ui/primitives/PageSkeleton';
import {ModalPresence} from '@ui/local/ModalPresence';

// Порог push-режима AI-панели. С гистерезисом: чтобы не «дребезжать» возле
// границы при ресайзе окна, после входа в push-режим требуем «запас» 60px,
// иначе возвращаемся в overlay.
const PUSH_ENTER = 860; // включаем push, когда доступно ≥ 860px без панели
const PUSH_EXIT = 800; // выходим в overlay, когда осталось ≤ 800px
const AI_PANEL_WIDTH = 400;

const DashCallModal = lazy(() =>
  import('@features/view-call-detail').then((module) => ({
    default: module.DashCallModal,
  })),
);

const MeetingDetailModal = lazy(() =>
  import('@features/view-meeting-detail').then((module) => ({
    default: module.MeetingDetailModal,
  })),
);

/** Корневой layout (раунд 31). */
export const AppLayout = () => {
  const scope = useWorkspaceUiScope();
  const {pathname} = useLocation();
  // Время в сервисе для раздела «Метрики» (только видимая активная вкладка).
  useActivityHeartbeat();
  useProductEventsFlush();
  const {currentUser} = useAuth();
  // Операторская консоль не требует существования хотя бы одного workspace.
  const operatorRoute =
    Boolean(currentUser?.isOperator) && pathname.startsWith('/admin');
  const aiOpen = useAtomValue(aiPanelOpenAtom);
  const callOpen = useAtomValue(dashCallOpenAtom);
  const meetingOpen = useAtomValue(meetingDetailOpenAtom);
  const [offline, setOffline] = useAtom(offlineModeAtom);
  const [contentWidth, setContentWidth] = useAtom(contentWidthAtom);
  const [pushMode, setPushMode] = useAtom(aiPushModeAtom);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContentWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [setContentWidth]);

  // Гистерезис: вычисляем целевой push-mode на основе текущего состояния
  // и направления изменения. Это убирает «танец» таблиц у границы.
  useEffect(() => {
    if (!aiOpen) {
      if (pushMode) setPushMode(false);
      return;
    }
    const free = contentWidth - AI_PANEL_WIDTH;
    if (!pushMode && free >= PUSH_ENTER) setPushMode(true);
    else if (pushMode && free < PUSH_EXIT) setPushMode(false);
  }, [aiOpen, contentWidth, pushMode, setPushMode]);

  return (
    <div className='flex min-h-screen bg-[var(--color-background)]'>
      <ThemeController />
      <Sidebar />

      <div
        ref={wrapperRef}
        className='flex flex-col flex-1 min-w-0'
        style={{
          marginLeft: 'var(--sidebar-w)',
          marginRight: pushMode ? `${AI_PANEL_WIDTH}px` : 0,
          marginTop: 'var(--header-offset)',
          transition: 'margin-right 200ms ease-out',
        }}
      >
        {/* Единый центрированный контейнер контента: max-width 1600px, mx-auto
            (было 1200px — область расширена, боковой отступ на широких экранах
            ~100px вместо ~300px). Внутренний padding тоже уменьшен втрое
            относительно исходных px-4/md:px-6. */}
        <main className='flex-1 w-full min-w-0 max-w-[1600px] mx-auto px-[calc(1rem/3)] md:px-2 pt-3 md:pt-4 pb-6 flex flex-col gap-4'>
          {/* Заголовок страницы — теперь в нормальном потоке, скроллится */}
          <Topbar />
          <OfflineBanner />
          {scope.canRender || operatorRoute ? (
            <Outlet key={scope.signature} />
          ) : scope.status === 'error' || scope.status === 'denied' ? (
            <ErrorState
              title={
                scope.status === 'denied'
                  ? 'Нет доступа к воркспейсу'
                  : 'Не удалось загрузить доступ'
              }
              onRetry={scope.retry}
            />
          ) : (
            <PageSkeleton />
          )}
        </main>

        <AppFooter />
      </div>

      {/* Глобальные панели и модалки */}
      <NotificationsDrawer motionKey={scope.signature} />
      <NotificationSettingsModal />
      <AiPanel motionKey={scope.signature} />
      {scope.canRender && (
        <>
          <TaskCreateModal key={`create:${scope.signature}`} />
          <TaskDetailModal key={`task:${scope.signature}`} />
          <ManagerDetailModal key={`manager:${scope.signature}`} />
          <ModalPresence key={`call-motion:${scope.signature}`}>
            {callOpen && (
              <Suspense fallback={null}>
                <DashCallModal key={`call:${scope.signature}`} />
              </Suspense>
            )}
          </ModalPresence>
          {/* Встреча — на странице «Встречи» и из очереди внимания на дашборде. */}
          <ModalPresence key={`meeting-motion:${scope.signature}`}>
            {meetingOpen && (
              <Suspense fallback={null}>
                <MeetingDetailModal key={`meeting:${scope.signature}`} />
              </Suspense>
            )}
          </ModalPresence>
        </>
      )}
      <CookieBanner />

      {/* Полноэкранная страница «Нет интернета» (demo-офлайн / реальная потеря сети) */}
      {offline && <OfflinePage onRetry={() => setOffline(false)} />}
    </div>
  );
};
